import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useAuth, AuthProvider } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';
import { POSProvider } from './context/POSContext';
import { OverrideProvider } from './context/OverrideContext';
import { LocalServerProvider } from './context/LocalServerContext';
import { Toaster, toast } from 'react-hot-toast';
import { X } from 'lucide-react';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import LicenseLockedOverlay from './components/common/LicenseLockedOverlay';
import PageLoader from './components/PageLoader';
import { PRODUCT_NAME } from './config/branding';

// Layout shells — statiques car toujours nécessaires
import DashboardLayout from './components/layout/DashboardLayout';
import POSLayout from './components/layout/POSLayout';

// ─── Lazy pages ───────────────────────────────────────────────────────────────
const Login                  = lazy(() => import('./pages/Login'));
const NotFound               = lazy(() => import('./pages/NotFound'));
const Dashboard              = lazy(() => import('./pages/Dashboard'));

// Auth / POS entry
const POSRestaurantLogin     = lazy(() => import('./pages/pos/POSRestaurantLogin'));
const POSRestaurantEntry     = lazy(() => import('./pages/pos/POSRestaurantEntry'));
const POSAdminLogin          = lazy(() => import('./pages/pos/POSAdminLogin'));
const POSSelectionScreen     = lazy(() => import('./pages/pos/POSSelectionScreen'));

// POS core
const POSMobileServer        = lazy(() => import('./pages/pos/POSMobileServer'));
const WebPOS                 = lazy(() => import('./pages/pos/WebPOS'));
const POSOrderScreen         = lazy(() => import('./pages/pos/POSOrderScreen'));
const POSPaymentPage         = lazy(() => import('./pages/pos/POSPaymentPage'));
const POSTransferPage        = lazy(() => import('./pages/pos/POSTransferPage'));
const POSIndividualPaymentPage = lazy(() => import('./pages/pos/POSIndividualPaymentPage'));
const POSTransferTablePage   = lazy(() => import('./pages/pos/POSTransferTablePage'));
const POSTransferServerPage  = lazy(() => import('./pages/pos/POSTransferServerPage'));
const POSGarnishManagementPage = lazy(() => import('./pages/pos/POSGarnishManagementPage'));
const KDS                    = lazy(() => import('./pages/pos/KDS'));

// POS config
const RestaurantDashboard    = lazy(() => import('./pages/pos/RestaurantDashboard'));
const POSRestaurantDetails   = lazy(() => import('./pages/pos/POSRestaurantDetails'));
const POSOpeningHours        = lazy(() => import('./pages/pos/POSOpeningHours'));
const POSUsers               = lazy(() => import('./pages/pos/POSUsers'));
const POSAccessRights        = lazy(() => import('./pages/pos/POSAccessRights'));
const POSCustomerReviewQR    = lazy(() => import('./pages/pos/POSCustomerReviewQR'));
const POSCategories          = lazy(() => import('./pages/pos/POSCategories'));
const POSProducts            = lazy(() => import('./pages/pos/POSProducts'));
const POSInventory           = lazy(() => import('./pages/pos/POSInventory'));
const KitchenPreparation     = lazy(() => import('./pages/pos/KitchenPreparation'));
const POSFloorPlan           = lazy(() => import('./pages/pos/POSFloorPlan'));
const ReservationList        = lazy(() => import('./pages/pos/ReservationList'));
const POSAnalysisPage        = lazy(() => import('./pages/pos/POSAnalysisPage'));
const POSSalesRevenuePage    = lazy(() => import('./pages/pos/POSSalesRevenuePage'));
const POSPrinters            = lazy(() => import('./pages/pos/POSPrinters'));
const POSDisplayScreens      = lazy(() => import('./pages/pos/POSDisplayScreens'));
const POSTaxes               = lazy(() => import('./pages/pos/POSTaxes'));
const POSPaymentMethods      = lazy(() => import('./pages/pos/POSPaymentMethods'));
const POSManagerComptable    = lazy(() => import('./pages/pos/POSManagerComptable'));
const POSManage              = lazy(() => import('./pages/pos/POSManage'));
const POSSessions            = lazy(() => import('./pages/pos/POSSessions'));
const POSTimeClock           = lazy(() => import('./pages/pos/POSTimeClock'));
const POSTimeclockManager    = lazy(() => import('./pages/pos/POSTimeclockManager'));

// Purchasing
const POSSuppliers           = lazy(() => import('./pages/pos/purchases/POSSuppliers'));
const POSPurchaseOrders      = lazy(() => import('./pages/pos/purchases/POSPurchaseOrders'));
const POSCreatePurchaseOrder = lazy(() => import('./pages/pos/purchases/POSCreatePurchaseOrder'));
const POSPurchasingAnalytics = lazy(() => import('./pages/pos/purchases/POSPurchasingAnalytics'));
const POSPurchaseReceptions  = lazy(() => import('./pages/pos/purchases/POSPurchaseReceptions'));
const POSViewReception       = lazy(() => import('./pages/pos/purchases/POSViewReception'));
const POSCreateReception     = lazy(() => import('./pages/pos/purchases/POSCreateReception'));
const POSPurchaseInvoices    = lazy(() => import('./pages/pos/purchases/POSPurchaseInvoices'));
const POSCreateInvoice       = lazy(() => import('./pages/pos/purchases/POSCreateInvoice'));
const POSSupplierPayments    = lazy(() => import('./pages/pos/purchases/POSSupplierPayments'));
const POSCashDiscrepancies   = lazy(() => import('./pages/pos/POSCashDiscrepancies'));
const POSCashActivityReport  = lazy(() => import('./pages/pos/POSCashActivityReport'));
const POSStockTransfers      = lazy(() => import('./pages/pos/purchases/POSStockTransfers'));
const POSStockAlerts         = lazy(() => import('./pages/pos/purchases/POSStockAlerts'));
const POSWarehouses          = lazy(() => import('./pages/pos/purchases/POSWarehouses'));
const POSInventoryCounts     = lazy(() => import('./pages/pos/purchases/POSInventoryCounts'));
const POSInventoryCountForm  = lazy(() => import('./pages/pos/purchases/POSInventoryCountForm'));

// Admin dashboard
const Restaurants            = lazy(() => import('./pages/restaurants/Restaurants'));
const CreateRestaurant       = lazy(() => import('./pages/restaurants/CreateRestaurant'));
const RestaurantAdminDetail  = lazy(() => import('./pages/restaurants/RestaurantAdminDetail'));
const RestaurantMenuBuilder  = lazy(() => import('./pages/restaurants/RestaurantMenuBuilder'));
const Orders                 = lazy(() => import('./pages/orders/Orders'));
const UsersPage              = lazy(() => import('./pages/users/Users'));
const Partners               = lazy(() => import('./pages/partners/Partners'));
const PartnerDetailPage      = lazy(() => import('./pages/partners/PartnerDetailPage'));
const PartnerProfile         = lazy(() => import('./pages/partners/PartnerProfile'));
const ManagersPage           = lazy(() => import('./pages/partners/ManagersPage'));
const ProspectingPage        = lazy(() => import('./pages/partners/ProspectingPage'));
const Plans                  = lazy(() => import('./pages/plans/Plans'));
const IncidentsPage          = lazy(() => import('./pages/system/Incidents'));
const AnalyticsPage          = lazy(() => import('./pages/system/Analytics'));
const PushNotificationsPage  = lazy(() => import('./pages/notifications/PushNotificationsPage'));
const AccountantsPage        = lazy(() => import('./pages/accountants/AccountantsPage'));
const CRMPage                = lazy(() => import('./pages/crm/CRMPage'));
const FiscalSettingsPage     = lazy(() => import('./pages/settings/FiscalSettingsPage'));
const PlatformSettings       = lazy(() => import('./pages/settings/PlatformSettings'));
const DebtsPage              = lazy(() => import('./pages/debts/DebtsPage'));

// ─── Route guards ─────────────────────────────────────────────────────────────

const ProtectedRoute = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) return <PageLoader fullscreen />;
  if (!isAuthenticated) {
    // Local mode: always redirect to POS entry (no admin dashboard)
    if (localMode) return <Navigate to="/pos/connect" replace />;

    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    const isIpAddress = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.test(hostname);
    let subdomain = (!isLocalhost && !isIpAddress && parts.length > 1) ? parts[0] : null;
    if (parts.length > 1 && parts[parts.length - 1] === 'localhost') subdomain = parts[0];

    if (subdomain && !['www', 'app', 'admin', 'partner'].includes(subdomain))
      return <Navigate to="/pos/login" replace />;
    if (window.location.pathname.startsWith('/partner'))
      return <Navigate to="/partner/login" replace />;
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
};

const RoleProtectedRoute = ({ allowedRoles }: { allowedRoles: string[] }) => {
  const { user, isLoading } = useAuth();
  if (isLoading) return <PageLoader fullscreen />;
  if (!user || !allowedRoles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return <Outlet />;
};

const POSContextLayout = () => (
  <ErrorBoundary>
    <POSProvider>
      <OverrideProvider>
        <Outlet />
      </OverrideProvider>
    </POSProvider>
  </ErrorBoundary>
);

const localMode = import.meta.env.VITE_LOCAL_MODE === 'true';

const RootRedirect = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
  const isIpAddress = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.test(hostname);
  let subdomain = (!isLocalhost && !isIpAddress && parts.length > 1) ? parts[0] : null;
  if (parts.length > 1 && parts[parts.length - 1] === 'localhost') subdomain = parts[0];
  if (isLoading) return <PageLoader fullscreen />;

  // Local server mode: skip the admin dashboard, go straight to POS entry
  if (localMode)
    return <Navigate to={isAuthenticated ? '/pos/dashboard' : '/pos/connect'} replace />;

  if (subdomain && !['www', 'app', 'admin', 'partner'].includes(subdomain))
    return <Navigate to={isAuthenticated ? '/pos/dashboard' : '/pos/login'} replace />;

  return <Navigate to="/dashboard" replace />;
};

// ─── Routes ───────────────────────────────────────────────────────────────────

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader fullscreen />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/partner/login" element={<Login />} />

        <Route element={<POSContextLayout />}>
          <Route path="/pos/connect" element={<POSRestaurantEntry />} />
          <Route path="/pos/admin/login" element={<POSAdminLogin />} />
          <Route path="/pos/:restaurantId/admin/login" element={<POSAdminLogin />} />
          <Route path="/pos/login" element={<POSRestaurantLogin />} />
          <Route path="/pos/:restaurantId/login" element={<POSRestaurantLogin />} />
          <Route path="/pos/select" element={<POSSelectionScreen />} />
        </Route>

        <Route path="/pos" element={<Navigate to="/pos/login" replace />} />
        <Route path="/pos/" element={<Navigate to="/pos/login" replace />} />

        <Route element={<ProtectedRoute />}>
          {/* Web POS */}
          <Route element={<POSContextLayout />}>
            <Route path="/pos/ui" element={<POSOrderScreen />} />
            <Route path="/pos/payment/:orderId" element={<POSPaymentPage />} />
            <Route path="/pos/transfer/:tableId" element={<POSTransferPage />} />
            <Route path="/pos/individual-payment/:orderId" element={<POSIndividualPaymentPage />} />
            <Route path="/pos/transfer-table/:tableId" element={<POSTransferTablePage />} />
            <Route path="/pos/transfer-server" element={<POSTransferServerPage />} />
            <Route path="/pos/server/mobile" element={<POSMobileServer />} />
            <Route path="/pos/garnishes/:productId" element={<POSGarnishManagementPage />} />
            <Route path="/pos/tables" element={<WebPOS />} />
            <Route path="/pos/kds" element={<KDS />} />
          </Route>

          {/* POS Configuration */}
          <Route element={<POSLayout />}>
            <Route path="/pos/dashboard" element={<RestaurantDashboard />} />
            <Route path="/pos/config/details" element={<POSRestaurantDetails />} />
            <Route path="/pos/config/hours" element={<POSOpeningHours />} />
            <Route path="/pos/config/users" element={<POSUsers />} />
            <Route path="/pos/config/access-rights" element={<POSAccessRights />} />
            <Route path="/pos/config/customer-review-qr" element={<POSCustomerReviewQR />} />
            <Route path="/pos/categories" element={<POSCategories />} />
            <Route path="/pos/products" element={<POSProducts />} />
            <Route path="/pos/inventory" element={<POSInventory />} />
            <Route path="/pos/production" element={<KitchenPreparation />} />
            <Route path="/pos/floor-plan" element={<POSFloorPlan />} />
            <Route path="/pos/reservations" element={<ReservationList />} />
            <Route path="/pos/analysis" element={<POSAnalysisPage />} />
            <Route path="/pos/analysis/sales" element={<POSSalesRevenuePage />} />
            <Route element={<RoleProtectedRoute allowedRoles={['partner', 'admin', 'manager', 'super_admin']} />}>
              <Route path="/pos/analysis/cash-discrepancies" element={<POSCashDiscrepancies />} />
              <Route path="/pos/analysis/cash-activity" element={<POSCashActivityReport />} />
              <Route path="/pos/purchases/payments" element={<POSSupplierPayments />} />
            </Route>
            <Route path="/pos/printers" element={<POSPrinters />} />
            <Route path="/pos/displays" element={<POSDisplayScreens />} />
            <Route path="/pos/config/taxes" element={<POSTaxes />} />
            <Route path="/pos/config/payment-methods" element={<POSPaymentMethods />} />
            <Route path="/pos/config/comptable" element={<POSManagerComptable />} />
            <Route path="/pos/purchases/orders" element={<POSPurchaseOrders />} />
            <Route path="/pos/purchases/orders/new" element={<POSCreatePurchaseOrder />} />
            <Route path="/pos/purchases/orders/:id" element={<POSCreatePurchaseOrder />} />
            <Route path="/pos/purchases/analytics" element={<POSPurchasingAnalytics />} />
            <Route path="/pos/purchases/receptions" element={<POSPurchaseReceptions />} />
            <Route path="/pos/purchases/receptions/new" element={<POSCreateReception />} />
            <Route path="/pos/purchases/receptions/:id" element={<POSCreateReception />} />
            <Route path="/pos/purchases/receptions/:id/print" element={<POSViewReception />} />
            <Route path="/pos/purchases/invoices" element={<POSPurchaseInvoices />} />
            <Route path="/pos/purchases/invoices/new" element={<POSCreateInvoice />} />
            <Route path="/pos/purchases/suppliers" element={<POSSuppliers />} />
            <Route path="/pos/purchases/transfers" element={<POSStockTransfers />} />
            <Route path="/pos/purchases/alerts" element={<POSStockAlerts />} />
            <Route path="/pos/inventory/warehouses" element={<POSWarehouses />} />
            <Route path="/pos/inventory/counts" element={<POSInventoryCounts />} />
            <Route path="/pos/inventory/counts/:id" element={<POSInventoryCountForm />} />
            <Route path="/pos/manage" element={<POSManage />} />
            <Route path="/pos/sessions" element={<POSSessions />} />
            <Route path="/pos/timeclock" element={<POSTimeClock />} />
            <Route path="/pos/timeclock/manager" element={<POSTimeclockManager />} />
            <Route path="/pos/settings/fiscal" element={<FiscalSettingsPage />} />
            <Route path="/pos/debts" element={<DebtsPage />} />
          </Route>

          {/* Admin Dashboard */}
          <Route element={<DashboardLayout />}>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/restaurants" element={<Restaurants />} />
            <Route path="/restaurants/new" element={<CreateRestaurant />} />
            <Route path="/restaurants/:id/edit" element={<CreateRestaurant />} />
            <Route path="/restaurants/:id/menu" element={<RestaurantMenuBuilder />} />
            <Route path="/orders" element={<Orders />} />
            <Route path="/partner/profile" element={<PartnerProfile />} />
            <Route path="/partner/managers" element={<ManagersPage />} />
            <Route path="/partner/prospecting" element={<ProspectingPage />} />

            <Route element={<RoleProtectedRoute allowedRoles={['super_admin']} />}>
              <Route path="/restaurants/:id/admin-detail" element={<RestaurantAdminDetail />} />
              <Route path="/partners" element={<Partners />} />
              <Route path="/partners/:id" element={<PartnerDetailPage />} />
              <Route path="/users" element={<UsersPage />} />
              <Route path="/plans" element={<Plans />} />
              <Route path="/incidents" element={<IncidentsPage />} />
              <Route path="/system/analytics" element={<AnalyticsPage />} />
              <Route path="/push-notifications" element={<PushNotificationsPage />} />
              <Route path="/accountants" element={<AccountantsPage />} />
              <Route path="/crm" element={<CRMPage />} />
              <Route path="/settings/platform" element={<PlatformSettings />} />
            </Route>

            <Route path="/system" element={<div>System Page</div>} />
            <Route path="/support" element={<div>Support Page</div>} />
          </Route>
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

// ─── App root ─────────────────────────────────────────────────────────────────

function App() {
  useEffect(() => {
    document.title = `${PRODUCT_NAME} - Restaurant Management`;
  }, []);

  return (
    <Router>
      <LocalServerProvider>
      <AuthProvider>
        <SocketProvider>
          <ErrorBoundary>
            <AppRoutes />
          </ErrorBoundary>
        </SocketProvider>
        <LicenseLockedOverlay />
        <div className="print:hidden">
          <Toaster
            position="top-right" 
            toastOptions={{
              duration: 2000,
              style: {
                borderRadius: '12px',
                padding: '12px 16px',
                fontSize: '14px',
                fontWeight: '500',
              }
            }}
          >
            {(t) => (
              <div
                onClick={() => toast.dismiss(t.id)}
                className={`${
                  t.visible ? 'animate-in fade-in zoom-in-95' : 'animate-out fade-out zoom-out-95'
                } max-w-md w-full bg-white shadow-lg rounded-xl pointer-events-auto flex ring-1 ring-black ring-opacity-5 cursor-pointer hover:shadow-xl transition-shadow`}
              >
                <div className="flex-1 w-0 p-4">
                  <div className="flex items-start">
                    <div className="ml-3 flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        {t.message as React.ReactNode}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex border-l border-gray-200">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toast.dismiss(t.id);
                    }}
                    className="w-16 border border-transparent rounded-none rounded-r-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-50 focus:outline-none transition-all"
                    title="Fermer"
                  >
                    <X size={20} strokeWidth={2.5} />
                  </button>
                </div>
              </div>
            )}
          </Toaster>
        </div>
      </AuthProvider>
      </LocalServerProvider>
    </Router>
  );
}

export default App;
